//  components/ui/Avatar.jsx
import React, { useMemo, useState } from "react";

function getInitials(name = "", max = 2) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((p) => p.normalize("NFKD"));
  if (parts.length === 1) {
    const token = parts[0].replace(/[^A-Za-z0-9]/g, "");
    return token.slice(0, max).toUpperCase();
  }
  const first = parts[0].replace(/[^A-Za-z0-9]/g, "");
  const last = parts[parts.length - 1].replace(/[^A-Za-z0-9]/g, "");
  return (first[0] || "?").toUpperCase() + (last[0] || "?").toUpperCase();
}

const TEXT_COLORS = [
  "#16a34a",
  "#f59e0b",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#22d3ee",
  "#84cc16",
  "#f97316",
  "#14b8a6",
];

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function lightenColor(hex, percent = 80) {
  const num = parseInt(hex.replace("#", ""), 16);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));
  return `rgb(${r}, ${g}, ${b})`;
}

export default function Avatar({ name, imageUrl, size = 40, className = "", title }) {
  const [imgOk, setImgOk] = useState(Boolean(imageUrl));

  const { initials, bg, fg } = useMemo(() => {
    const inits = getInitials(name, 2);
    const idx = hashCode(name || "default") % TEXT_COLORS.length;
    const fgHex = TEXT_COLORS[idx];
    const bgHex = lightenColor(fgHex, 80);
    return {
      initials: inits,
      bg: bgHex,
      fg: fgHex,
    };
  }, [name]);

  const dimension = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };
  const fontSize = Math.max(12, Math.floor(size * 0.42));

  const showInitials = !imageUrl || !imgOk;

  return (
    <div
      className={`inline-flex items-center justify-center rounded-full overflow-hidden ${className}`}
      style={dimension}
      title={title || name || ""}
      aria-label={name || "avatar"}
    >
      {showInitials ? (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ backgroundColor: bg, color: fg, fontSize, fontWeight: 600 }}
        >
          {initials}
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={name || "avatar"}
          className="w-full h-full object-cover"
          onError={() => setImgOk(false)}
          onLoad={() => setImgOk(true)}
        />
      )}
    </div>
  );
}
